/**
 * @fileoverview 全局应用状态管理
 * @description 使用 Zustand 管理全局应用状态，包括用户信息、认证状态等
 * 已接入后端 API：通过 wagmi 钱包签名登录，Token 持久化到 localStorage
 * MVC 架构：Store 层，管理认证状态，协调 API 调用
 * @module stores/useAppStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchUserInfo, fetchUserBalance } from '../services/userService';
import { saveToken, clearToken, getToken, API_OK } from '../services/api';
import type { User } from '../types';
import type { LoginResponseData } from '../services/authService';

// ============================================================
// Store 类型定义
// ============================================================

/**
 * 应用全局状态接口
 */
interface AppState {
  /** 当前登录用户信息，null 表示未登录 */
  currentUser: User | null;
  /** 是否已登录 */
  isAuthenticated: boolean;
  /** 全局加载状态 */
  isLoading: boolean;
  /** 侧边栏展开状态（移动端） */
  isSidebarOpen: boolean;
}

/**
 * 应用全局操作接口
 */
interface AppActions {
  /**
   * 钱包登录成功后，保存用户信息并更新认证状态
   * @param loginData - 后端登录接口返回的用户数据
   */
  loginWithWallet: (loginData: LoginResponseData) => Promise<void>;

  /**
   * 兼容旧版调用的登录函数（Login.tsx 中使用）
   * @deprecated 请使用 loginWithWallet 替代
   * @param _identifier - 用户标识（钱包地址或 Telegram 用户名）
   * @param _type - 登录类型
   */
  login: (identifier: string, type: string) => Promise<boolean>;

  /**
   * 用户登出，清除登录状态和 Token
   */
  logout: () => void;

  /**
   * 从后端刷新用户信息（余额等实时数据）
   */
  refreshUserInfo: () => Promise<void>;

  /**
   * 更新用户信息（部分更新）
   * @param updates - 需要更新的用户字段
   */
  updateUser: (updates: Partial<User>) => void;

  /**
   * 设置全局加载状态
   * @param loading - 是否加载中
   */
  setLoading: (loading: boolean) => void;

  /**
   * 切换侧边栏展开/收起状态
   */
  toggleSidebar: () => void;

  /**
   * 设置侧边栏状态
   * @param open - 是否展开
   */
  setSidebarOpen: (open: boolean) => void;
}

type AppStore = AppState & AppActions;

// ============================================================
// 工具函数：后端数据 → 前端 User 类型映射
// ============================================================

/**
 * 将后端用户数据映射为前端 User 类型
 * @description 后端缺失的字段使用默认值填充，不影响 UI 展示
 * @param loginData - 后端登录返回数据
 * @param balance - 余额（可选，来自 userInfo 接口）
 * @returns 前端 User 对象
 */
function mapLoginDataToUser(loginData: LoginResponseData, balance?: number): User {
  const { account, accountName, chain, email, referralCode } = loginData;
  // guid 可能是字符串（Sequelize BIGINT 序列化）或数字，统一转为数字
  const guid = loginData.guid != null ? Number(loginData.guid) : undefined;

  // account 可能是钱包地址（0x...）或 TG 用户 ID（数字），统一转字符串处理
  const accountStr = account != null ? String(account) : '';
  const isWalletAddress = accountStr.startsWith('0x') && accountStr.length >= 10;

  // 生成显示名：优先用 accountName，其次截断钱包地址，TG 用户直接用 accountStr
  const displayName =
    accountName && accountName !== accountStr
      ? accountName
      : isWalletAddress
      ? `${accountStr.slice(0, 6)}...${accountStr.slice(-4)}`
      : accountStr
      ? accountStr
      : `User#${guid}`;

  return {
    id: accountStr || String(guid),
    guid: guid,
    username: displayName,
    walletAddress: isWalletAddress ? accountStr : undefined,
    email: email || undefined,
    // 以下字段后端未提供，使用默认值（后续可通过 refreshUserInfo 更新）
    phone: undefined,
    realName: undefined,
    avatar: undefined,
    balance: balance ?? loginData.balance ?? 0,
    totalPnl: 0,          // 后端缺失，待后端补充
    totalPnlPercent: 0,   // 后端缺失，待后端补充
    createdAt: new Date().toISOString(),
    telegramBound: false, // 后端缺失，待后端补充
    telegramUsername: undefined,
    vipLevel: 0,          // 已移除 VIP 显示，保留字段兼容性
    frozenBalance: 0,     // 后端缺失，待后端补充
    totalDeposit: 0,      // 后端缺失，可前端聚合 depositHistory
    totalWithdraw: 0,     // 后端缺失，可前端聚合 withdrawHistory
    profilePoints: 0,     // 后端缺失，待后端补充
    loginType: chain === 'ton' ? 'wallet' : 'wallet',
  };
}

// ============================================================
// Store 实现
// ============================================================

/**
 * 全局应用状态 Store
 * 使用 persist 中间件将认证状态持久化到 localStorage
 */
export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      currentUser: null,
      isAuthenticated: false,
      isLoading: false,
      isSidebarOpen: false,

      /**
       * 钱包登录成功后保存用户信息
       * 由 useWalletLogin Hook 在签名验证成功后调用
       */
      loginWithWallet: async (loginData: LoginResponseData) => {
        set({ isLoading: true });

        try {
          // Token 已在 useWalletLogin 中保存，这里直接拉取用户信息
          const userInfoResp = await fetchUserInfo();
          let balance = loginData.balance;

          if (userInfoResp.status === API_OK && userInfoResp.data) {
            balance = userInfoResp.data.balance ?? balance;
          }

          const user = mapLoginDataToUser(loginData, balance);
          set({
            currentUser: user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // 即使拉取用户信息失败，也使用登录返回的基础数据
          const user = mapLoginDataToUser(loginData);
          set({
            currentUser: user,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      },

      /**
       * 兼容旧版 Login.tsx 的登录函数
       * 在真实钱包签名流程完成前，用于 UI 状态更新
       */
      login: async (identifier: string, _type: string) => {
        set({ isLoading: true });
        // 简单设置一个临时用户状态（真实数据由 loginWithWallet 设置）
        const tempUser: User = {
          id: identifier,
          username: identifier.startsWith('0x')
            ? `${identifier.slice(0, 6)}...${identifier.slice(-4)}`
            : identifier,
          walletAddress: identifier.startsWith('0x') ? identifier : undefined,
          balance: 0,
          totalPnl: 0,
          totalPnlPercent: 0,
          createdAt: new Date().toISOString(),
          telegramBound: false,
          vipLevel: 0,
          frozenBalance: 0,
          totalDeposit: 0,
          totalWithdraw: 0,
          loginType: 'wallet',
        };
        set({
          currentUser: tempUser,
          isAuthenticated: true,
          isLoading: false,
        });
        return true;
      },

      /**
       * 登出逻辑，清除用户状态和 Token
       */
      logout: () => {
        clearToken();
        set({
          currentUser: null,
          isAuthenticated: false,
        });
      },

      /**
       * 从后端刷新用户信息（余额 + guid 等实时数据）
       * 在钱包页等需要实时余额的场景调用
       */
      refreshUserInfo: async () => {
        const token = getToken();
        if (!token) return;

        try {
          // 优先使用 userInfo 接口，同步 guid 和余额
          const resp = await fetchUserInfo();
          if (resp.status === API_OK && resp.data) {
            const { currentUser } = get();
            if (currentUser) {
              const newGuid = resp.data.guid != null ? Number(resp.data.guid) : currentUser.guid;
              set({
                currentUser: {
                  ...currentUser,
                  balance: resp.data.balance ?? currentUser.balance,
                  guid: newGuid,
                },
              });
            }
          } else {
            // fallback：使用轻量级 balance 接口
            const balanceResp = await fetchUserBalance();
            if (balanceResp.status === API_OK && balanceResp.data !== undefined && balanceResp.data !== null) {
              const { currentUser } = get();
              if (currentUser) {
                set({
                  currentUser: {
                    ...currentUser,
                    balance: balanceResp.data,
                  },
                });
              }
            }
          }
        } catch {
          // 静默失败，不影响用户体验
        }
      },

      /**
       * 更新用户信息（部分更新）
       */
      updateUser: (updates: Partial<User>) => {
        set((state) => ({
          currentUser: state.currentUser
            ? { ...state.currentUser, ...updates }
            : null,
        }));
      },

      /**
       * 设置全局加载状态
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * 切换侧边栏状态
       */
      toggleSidebar: () => {
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
      },

      /**
       * 直接设置侧边栏状态
       */
      setSidebarOpen: (open: boolean) => {
        set({ isSidebarOpen: open });
      },
    }),
    {
      name: 'pythia_auth',
      // 只持久化认证相关字段
      partialize: (state) => ({
        currentUser: state.currentUser,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
